// History of briefs (generated_at, period, model; tokens + cost for the super
// admin, which the server sends to nobody else) and the super admin's settings.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorText } from "@/lib/errorText";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { AlertTriangle } from "lucide-react";
import { AI } from "@/constants/testIds/ai";
import { fmtWhen } from "@/components/ai-analysis/aiText";

const STATUS_CLS = {
    done: "text-emerald-700 dark:text-emerald-300",
    failed: "text-red-700 dark:text-red-300",
    running: "text-amber-700 dark:text-amber-300",
    queued: "text-[color:var(--ink-muted)]",
};

// Costs are cents or less per brief: show enough decimals to be non-zero.
export function fmtUsd(v) {
    const n = Number(v || 0);
    return n >= 1 ? n.toFixed(2) : n >= 0.01 ? n.toFixed(3) : n.toFixed(4);
}

export function periodLabel(b, lang) {
    const p = b?.period || {};
    return (lang === "ar" ? p.label_ar : p.label_en) || p.start || "";
}

export function HistoryView({ briefs, selectedId, onSelect, isSuper, lang, t, monthSpend }) {
    return (
        <div data-testid={AI.history} className="space-y-2">
            {isSuper && monthSpend !== undefined ? (
                <p className="text-xs text-[color:var(--ink-muted)]">
                    {t("ai_month_spend")}: <span className="font-semibold tabular-nums">${fmtUsd(monthSpend)}</span>
                </p>
            ) : null}
            <ul className="divide-y divide-[color:var(--border-soft)] rounded-xl border border-[color:var(--border-soft)] surface">
                {briefs.map((b) => {
                    const tokens = b.usage ? (b.usage.input_tokens || 0) + (b.usage.output_tokens || 0)
                        + (b.usage.cache_read_input_tokens || 0) + (b.usage.cache_creation_input_tokens || 0) : null;
                    return (
                        <li key={b.id}>
                            <button type="button" data-testid={AI.historyRow(b.id)} onClick={() => b.status === "done" && onSelect(b.id)}
                                    className={`flex w-full flex-col gap-1 px-4 py-3 text-start transition-colors hover:bg-[color:var(--brand-orange-soft)] ${b.id === selectedId ? "bg-[color:var(--brand-orange-soft)]" : ""}`}>
                                <span className="flex flex-wrap items-center gap-2 text-sm">
                                    <span className="font-semibold text-[color:var(--ink)]">{t(`ai_kind_${b.kind}`)}</span>
                                    <span className="text-[color:var(--ink-muted)]">{periodLabel(b, lang)}</span>
                                    <span className={`text-xs font-semibold ${STATUS_CLS[b.status] || ""}`}>{t(`ai_status_${b.status}`)}</span>
                                    {b.status === "done" && (b.errors || []).length ? (
                                        <span data-testid={AI.historyPartial} title={b.errors.map((e) => `${t(`ai_part_${e.part}`)}: ${lang === "ar" ? e.message_ar : e.message_en}`).join("\n")}
                                              className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                            <AlertTriangle className="h-3 w-3" />{t("ai_status_partial")}: {[...new Set(b.errors.map((e) => t(`ai_part_${e.part}`)))].join(lang === "ar" ? "، " : ", ")}
                                        </span>
                                    ) : null}
                                </span>
                                <span className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[color:var(--ink-faint)]">
                                    <span>{t("ai_generated")}: {fmtWhen(b.generated_at || b.created_at, lang)}</span>
                                    {b.models?.exec ? <span>{t("ai_model")}: {b.models.exec}{b.models.detail && b.models.detail !== b.models.exec ? ` + ${b.models.detail}` : ""}</span> : null}
                                    {isSuper && tokens ? <span>{t("ai_tokens")}: {tokens.toLocaleString("en-US")}</span> : null}
                                    {b.mode ? <span>{t(`ai_mode_${b.mode}`)}</span> : null}
                                    {isSuper && b.cost_usd !== undefined && b.cost_usd !== null ? <span>{t("ai_cost")}: ${fmtUsd(b.cost_usd)}</span> : null}
                                    {b.status === "failed" && b.error ? <span className="text-red-700">{lang === "ar" ? b.error.message_ar : b.error.message_en}</span> : null}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export function SettingsCard({ settings, onSaved, t }) {
    const [form, setForm] = useState(settings || {});
    const [saving, setSaving] = useState(false);
    // /ai/status is polled while a brief runs: refill the form from the server
    // only until the user starts editing (and again after a save).
    const dirty = useRef(false);
    useEffect(() => {
        if (!dirty.current) setForm(settings || {});
    }, [settings]);
    if (!settings) return null;
    const set = (k, v) => {
        dirty.current = true;
        setForm((f) => ({ ...f, [k]: v }));
    };
    const save = async () => {
        setSaving(true);
        try {
            const body = {
                exec_model: String(form.exec_model || "").trim(),
                detail_model: String(form.detail_model || "").trim(),
                daily_enabled: !!form.daily_enabled, weekly_enabled: !!form.weekly_enabled, monthly_enabled: !!form.monthly_enabled,
                batch_enabled: !!form.batch_enabled,
                cooldown_minutes: Math.max(0, Math.round(Number(form.cooldown_minutes) || 0)),
                monthly_budget_usd: Number(form.monthly_budget_usd) || 0,
            };
            await api.patch("/ai/settings", body);
            toast.success(t("ai_saved"));
            dirty.current = false;
            onSaved?.();
        } catch (e) {
            toast.error(apiErrorText(e));
        } finally {
            setSaving(false);
        }
    };
    return (
        <Card data-testid={AI.settings} className="surface">
            <CardContent className="space-y-3 p-4">
                <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("ai_settings")}</h3>
                <p className="text-[11px] leading-relaxed text-[color:var(--ink-faint)]">{t("ai_schedule_note")}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                        <Label className="text-xs">{t("ai_set_exec_model")}</Label>
                        <Input dir="ltr" value={form.exec_model || ""} onChange={(e) => set("exec_model", e.target.value)} />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">{t("ai_set_detail_model")}</Label>
                        <Input dir="ltr" value={form.detail_model || ""} onChange={(e) => set("detail_model", e.target.value)} />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">{t("ai_set_cooldown")}</Label>
                        <Input type="number" min={0} dir="ltr" value={form.cooldown_minutes ?? ""} onChange={(e) => set("cooldown_minutes", e.target.value)} />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">{t("ai_set_budget")}</Label>
                        <Input type="number" min={0} dir="ltr" value={form.monthly_budget_usd ?? ""} onChange={(e) => set("monthly_budget_usd", e.target.value)} />
                    </div>
                </div>
                <div className="flex flex-wrap gap-4">
                    {["daily", "weekly", "monthly", "batch"].map((k) => (
                        <label key={k} className="flex items-center gap-2 text-sm">
                            <Switch checked={!!form[`${k}_enabled`]} onCheckedChange={(v) => set(`${k}_enabled`, v)} />
                            {t(`ai_set_${k}`)}
                        </label>
                    ))}
                </div>
                <Button data-testid={AI.settingsSave} size="sm" onClick={save} disabled={saving}>{t("ai_save")}</Button>
            </CardContent>
        </Card>
    );
}
